import { type ForwardedRef, forwardRef, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ResetIcon } from '@dynatrace/strato-icons';

import { Button } from '../../buttons/button/Button.js';
import { useId } from '../../core/hooks/useId.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';

//#region Typings
/**
 * Accepted properties for the Chip Button.
 * @public
 */
export interface ResetButtonProps
  extends AriaLabelingProps,
    DOMProps,
    StylingProps,
    DataTestId,
    BehaviorTrackingProps {
  /** Handler that is called when the ResetButton is clicked. */
  onClick: () => void;
}
//#endregion Typings

/**
 * Component which allows resetting the values of the FilterBar items.
 * @public
 */
export const ResetButton = /* @__PURE__ */ forwardRef(
  (props: ResetButtonProps, forwardedRef: ForwardedRef<HTMLButtonElement>) => {
    const {
      onClick,
      'aria-labelledby': ariaLabelledByProp,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: ResetButtonProps = props;

    const intl = useIntl();
    const resetText = intl.formatMessage({
      defaultMessage: 'Reset filters',
      id: '21mG229oVCMPPsYY',
      description: 'Shown for the reset button of the FilterBar.',
    });

    const tooltipId = useId();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    const ariaLabelledBy = useMemo(() => {
      const parts = [
        isTooltipOpen ? tooltipId : '',
        ariaLabelledByProp ?? '',
      ].filter(Boolean);
      return parts.length ? parts.join(' ') : undefined;
    }, [ariaLabelledByProp, isTooltipOpen, tooltipId]);

    return (
      <Tooltip id={tooltipId} text={resetText} onOpenChange={setIsTooltipOpen}>
        <Button
          data-testid={dataTestId}
          ref={forwardedRef}
          variant="default"
          onClick={onClick}
          aria-label={resetText}
          aria-labelledby={ariaLabelledBy}
          className={consumerClassName}
          style={consumerStyle}
          {...remainingProps}
        >
          <Button.Prefix>
            <ResetIcon />
          </Button.Prefix>
        </Button>
      </Tooltip>
    );
  },
);

(ResetButton as typeof ResetButton & { displayName: string }).displayName =
  'FilterBar.ResetButton';
